import { Button, Flex, Text } from '@ifood/pomodoro-components';
import tokens from '@ifood/pomodoro-tokens';
import { Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl';
import React, { FC, useRef } from 'react';
import Recaptcha from 'react-google-recaptcha';
import { useMutation } from 'react-query';

import { RECAPTCHA_SITE_ID } from '@constants/env';
import { RECAPTCHA_ERROR, buildGetRecaptchaTokenByRef } from 'modules/Recaptcha';
import { ClientOnly, notifyError, notifySuccess } from 'modules/UI';
import { Input } from 'modules/UI/modules/Form';

import { createContact } from '../../services';
import {
  CreateContactMutation,
  NewsletterFormFields,
  NewsletterFormValidated,
  NewsletterFormValues,
} from '../../types';
import { buildNewsletterFormValidator } from '../../utils';
import { FormContainer, StyledDialog, Title } from './styles';

const initialFields: NewsletterFormValues = {
  email: '',
};

type PropTypes = {
  isOpen: boolean;
  onCloseClick: () => void;
};

export const NewsletterDialog: FC<PropTypes> = ({ isOpen, onCloseClick }) => {
  const ref = useRef<FormikProps<NewsletterFormValues>>(null);

  const { formatMessage } = useIntl();
  const intl = useIntl();

  const formValidator = buildNewsletterFormValidator(intl);

  const recaptchaRef = useRef<Recaptcha | null>(null);
  const getRecaptchaToken = buildGetRecaptchaTokenByRef(recaptchaRef);

  const createContactMutation = useMutation((mutation: CreateContactMutation) =>
    getRecaptchaToken()
      .then((token) => {
        if (!token) return Promise.reject(RECAPTCHA_ERROR);

        return token;
      })
      .then((token) => createContact(mutation, token))
      .then(() => {
        notifySuccess({ message: formatMessage({ id: 'success.create-contact' }) });
        ref.current?.resetForm();
        onCloseClick();
      })
      .catch((error) => {
        if (error === RECAPTCHA_ERROR) {
          notifyError({ message: formatMessage({ id: `error.${RECAPTCHA_ERROR}` }) });
          return;
        }

        notifyError({ message: formatMessage({ id: 'error.create-contact' }) });
      })
  );

  return (
    <>
      <ClientOnly>
        {RECAPTCHA_SITE_ID && isOpen && <Recaptcha size="invisible" sitekey={RECAPTCHA_SITE_ID} ref={recaptchaRef} />}
      </ClientOnly>
      <StyledDialog open={isOpen} onClose={onCloseClick}>
        <StyledDialog.Header>
          <Title>
            <FormattedMessage id="home.newsletter.title" />
          </Title>
        </StyledDialog.Header>

        <StyledDialog.Body>
          <Formik<NewsletterFormValues>
            onSubmit={({ email }: NewsletterFormValidated): Promise<void> =>
              createContactMutation.mutateAsync({ email })
            }
            initialValues={initialFields}
            validate={formValidator}
            innerRef={ref}
          >
            {({ isValid, handleSubmit }): JSX.Element => (
              <FormContainer onSubmit={handleSubmit}>
                <Text mb={tokens.space.larger}>
                  <FormattedMessage id="home.newsletter.description" />
                </Text>

                <Flex>
                  <Input name={NewsletterFormFields.email} placeholder={formatMessage({ id: 'fields.email' })} />

                  <Button
                    ml={tokens.space.regular}
                    type="submit"
                    variant="secondary"
                    disabled={!isValid}
                    height="50px"
                    isLoading={createContactMutation.isLoading}
                  >
                    <FormattedMessage id="submit" />
                  </Button>
                </Flex>
              </FormContainer>
            )}
          </Formik>
        </StyledDialog.Body>
      </StyledDialog>
    </>
  );
};
